import { Button, Center, Flex, Heading, Spinner, Stack, StackProps, Tag, useToast } from '@chakra-ui/react';
import { buildCargoString, copyText, Show } from '@pelicargo/ui';
import { DimensionUnit, WeightUnit } from '@prisma/client';
import { useCallback, useMemo } from 'react';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import { HiPlus } from 'react-icons/hi2';

import { useCargos } from '../../hooks/cargo/useCargos';
import { useCreateCargo } from '../../hooks/cargo/useCreate';
import { CargoOutput } from '../../hooks/cargo/useForm';
import { CargoListItem } from './CargoListItem';

type Props = StackProps & {
  requestId?: number;
};

export const CargoList = ({ requestId, ...rest }: Props) => {
  const [cargoList, isFetching] = useCargos(requestId);
  const { createDefault, isLoading: isCreating } = useCreateCargo(requestId);

  const handleCargoCreate = async () => {
    if (!requestId) return;
    await createDefault?.();
  };

  const renderCargo = (cargo: CargoOutput) => <CargoListItem key={cargo?.id} cargo={cargo} />;

  const isLoading = isFetching || requestId === undefined;
  const toast = useToast();

  const copyCargo = useCallback(() => {
    const text = buildCargoString(cargoList, DimensionUnit.in, WeightUnit.kg, DimensionUnit.in);
    copyText(text);
    toast({
      colorScheme: 'green',
      title: 'Copied!',
    });
  }, [cargoList, toast]);

  const cargoCount = useMemo(() => {
    return cargoList?.reduce((acc, cargo) => acc + cargo.quantity, 0) || 0;
  }, [cargoList]);

  return (
    <Stack direction="column" spacing={2} {...rest} role="group">
      <Stack direction="row" justifyContent="space-between" alignItems="center" px="2">
        <Stack direction="row" alignItems="center">
          <Heading size="xs">Cargo Items</Heading>
          <Tag size="sm">{cargoCount}</Tag>
        </Stack>
        <Flex>
          <Button
            size="xs"
            leftIcon={<HiOutlineClipboardCopy size={14} />}
            colorScheme="brand"
            variant="ghost"
            onClick={copyCargo}
            alignSelf="flex-end"
            opacity={0}
            _groupHover={{
              opacity: 1,
            }}
            height="16px"
          >
            Copy
          </Button>
          <Button
            size="xs"
            leftIcon={<HiPlus size={14} />}
            colorScheme="brand"
            variant="ghost"
            onClick={handleCargoCreate}
            isLoading={isCreating}
            alignSelf="flex-end"
            opacity={0}
            _groupHover={{
              opacity: 1,
            }}
            height="16px"
          >
            Add
          </Button>
        </Flex>
      </Stack>
      <Show if={isLoading}>
        <Center w="full" py={4}>
          <Spinner size="sm" />
        </Center>
      </Show>

      <Show if={!isLoading}>
        <Stack direction="column" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          {cargoList.map(renderCargo)}
        </Stack>
      </Show>
    </Stack>
  );
};
